// src/components/PageBuilder/AppPreview.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PreviewRenderer from './RightPanel/PreviewRenderer';
import { logger } from '../../utils/logger';
import { getLayoutSlots } from '../../config/layouts';

const getBackendUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  return apiUrl.replace(/\/api$/, '');
};

async function fetchAppPages(appName) {
  try {
    const url = `${getBackendUrl()}/api/pages/${appName}`;
    logger.log('🌐 [AppPreview] Fetching', { appName, url });
    const res = await fetch(url);
    if (!res.ok) {
      logger.error('❌ [AppPreview] Fetch failed', { status: res.status });
      return [];
    }
    const result = await res.json();
    const pages = result.data || result || [];
    logger.log('✅ [AppPreview] Fetched pages', { 
      count: pages.length,
      fullResponse: JSON.stringify(result, null, 2)
    });
    return pages;
  } catch (e) {
    logger.error('❌ [AppPreview] Error', e);
    return [];
  }
}

async function fetchPageByName(appName, pageName) {
  try {
    const url = `${getBackendUrl()}/api/pages/${appName}/${encodeURIComponent(pageName)}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const result = await res.json();
    return result.data || result;
  } catch (e) {
    logger.error('❌ [AppPreview] Failed to fetch page:', e);
    return null;
  }
}

const createSlotSkeleton = (layoutId, themeId, pageName) => {
  const slots = getLayoutSlots(layoutId);
  const skeleton = {};
  slots.forEach(slot => {
    skeleton[slot.id] = {
      components: [],
      layoutId,
      themeId,
      pageName
    };
  });
  return { skeleton, slots };
};

const assignComponentsToSlots = (slotMap, components, fallbackSlotId) => {
  const updated = { ...slotMap };
  const slotIds = Object.keys(updated);
  const defaultSlot = fallbackSlotId || slotIds[0];
  components.forEach(comp => {
    let slotId = comp.slotId;
    if (!slotId || !updated[slotId]) slotId = defaultSlot;
    updated[slotId] = {
      ...updated[slotId],
      components: [...(updated[slotId]?.components || []), { ...comp, slotId }]
    };
  });
  return updated;
};

const Tip = ({ bg, txt, children }) => (
  <div style={{ background: bg, color: txt, padding: 12, borderRadius: 8, fontSize: 14 }}>
    {children}
  </div>
);

export default function AppPreview({ appName }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slotContent, setSlotContent] = useState({});
  const [currentLayoutId, setCurrentLayoutId] = useState(null);
  const [currentThemeId, setCurrentThemeId] = useState(null);
  const [activePageName, setActivePageName] = useState(null);
  const initializeFromPage = useCallback((pageData) => {
    if (!pageData) return;
    const layoutId = pageData.layoutId || 'single-column';
    const themeId = pageData.themeId || 'modern';
    const pageLabel = pageData.pageName || pageData.name || pageData.id || 'Page';
    const { skeleton, slots } = createSlotSkeleton(layoutId, themeId, pageLabel);
    const fallbackSlotId =
      slots.find(s => s.id === 'content' || s.id === 'body' || s.required)?.id ||
      slots[0]?.id ||
      'body';
    const mappedSlots = assignComponentsToSlots(
      skeleton,
      pageData.components || [],
      fallbackSlotId
    );
    setSlotContent(mappedSlots);
    setCurrentLayoutId(layoutId);
    setCurrentThemeId(themeId);
    setActivePageName(pageLabel);
  }, []);

  useEffect(() => {
    if (!appName) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const loadPages = async () => {
      setLoading(true);
      try {
        const fetchedPages = await fetchAppPages(appName);
        if (cancelled) return;
        setPages(fetchedPages);
        const homePage = fetchedPages.find(page =>
          (page.pageName || page.name || '').toLowerCase() === 'home'
        ) || fetchedPages[0];

        if (homePage) {
          initializeFromPage(homePage);
        } else {
          setSlotContent({});
        }
      } catch (error) {
        logger.error('❌ [AppPreview] Failed to load pages:', error);
        setSlotContent({});
      } finally {
        setLoading(false);
      }
    };

    loadPages();
    return () => {
      cancelled = true;
    };
  }, [appName]);

  useEffect(() => {
    if (!appName) return;
    if (window.opener && window.opener !== window) {
      try {
        window.opener.postMessage(
          { type: 'REQUEST_PREVIEW_DATA', source: 'AppPreview' },
          window.location.origin
        );
        console.log('📨 [AppPreview] Requested preview data from opener');
      } catch (error) {
        console.warn('⚠️ [AppPreview] Failed to request preview data from opener', error);
      }
    }
  }, [appName]);

  // Listen for slot navigation events (backup handler - PreviewRenderer also handles this)
  // This ensures AppPreview state stays in sync even if PreviewRenderer's handler doesn't call onUpdateComponents
  useEffect(() => {
    if (!appName) {
      logger.warn('⚠️ [AppPreview] Event listener not set up - appName missing');
      return;
    }

    const handleSlotNavigation = async (event) => {
      // Check if event was already handled by PreviewRenderer
      // PreviewRenderer will call onUpdateComponents if provided, so this is mainly a backup
      if (event.detail?.handledByPreviewRenderer) {
        logger.log('ℹ️ [AppPreview] Slot navigation already handled by PreviewRenderer');
        return;
      }
      
      console.log('🎯 [AppPreview] Event received:', event);
      const { targetPage, targetSlotId, targetSlot, clearSlot } = event.detail || {};
      
      if (!targetPage || (!targetSlotId && !targetSlot)) {
        logger.warn('⚠️ [AppPreview] Slot navigation missing required params:', { 
          targetPage, 
          targetSlotId,
          targetSlot,
          eventDetail: event.detail 
        });
        return;
      }
      
      if (!appName) {
        logger.error('❌ [AppPreview] appName not available for slot navigation');
        return;
      }
      
      logger.log('🔗 [AppPreview] Slot navigation requested', { 
        targetPage, 
        targetSlotId, 
        targetSlot,
        clearSlot,
        appName 
      });
      
      // Fetch target page - try multiple name formats
      let targetPageData = await fetchPageByName(appName, targetPage);
      
      // If not found, try with pageName field
      if (!targetPageData && pages.length > 0) {
        const pageByName = pages.find(p => 
          (p.pageName || p.name || '').toLowerCase() === targetPage.toLowerCase() ||
          (p.id || '').toLowerCase() === targetPage.toLowerCase()
        );
        if (pageByName) {
          targetPageData = pageByName;
          logger.log('✅ [AppPreview] Found page in local cache', { targetPage });
        }
      }
      
      if (!targetPageData) {
        logger.error('❌ [AppPreview] Target page not found:', { 
          targetPage, 
          appName,
          availablePages: pages.map(p => p.pageName || p.name || p.id)
        });
        return;
      }
      
      // Get actual slot IDs from current layout
      const layoutSlots = currentLayoutId ? getLayoutSlots(currentLayoutId) : [];
      const validSlotIds = layoutSlots.map(s => s.id);
      const mainContentSlotId = layoutSlots.find(s => s.id === 'content' || s.id === 'body' || s.required)?.id || 
                                 layoutSlots[0]?.id || 'content';

      const resolveSlotId = () => {
        const slotRef = targetSlotId || targetSlot;
        if (!slotRef) return mainContentSlotId;
        
        // Check if slot exists in current layout
        if (validSlotIds.includes(slotRef)) {
          return slotRef;
        }
        
        // Try to find matching slot by name or common aliases
        const matchingSlot = layoutSlots.find(s => 
          s.id === slotRef || 
          s.name?.toLowerCase() === slotRef.toLowerCase() ||
          (slotRef === 'body' && (s.id === 'content' || s.id === 'body')) ||
          (slotRef === 'content' && (s.id === 'content' || s.id === 'body')) ||
          (slotRef === 'main-content' && (s.id === 'content' || s.id === 'body' || s.id === 'main-content'))
        );
        
        if (matchingSlot) {
          logger.log('🔄 [AppPreview] Resolved slot ID', { requested: slotRef, resolved: matchingSlot.id });
          return matchingSlot.id;
        }
        
        // Fallback to main content slot
        logger.warn('⚠️ [AppPreview] Slot not found, using default', { requested: slotRef, default: mainContentSlotId });
        return mainContentSlotId;
      };
      const resolvedSlotId = resolveSlotId();

      // Extract components for the target slot (match by actual slot ID)
      const allComponents = targetPageData.components || [];
      logger.log('📦 [AppPreview] Processing page components', {
        totalComponents: allComponents.length,
        inputSlot: targetSlotId || targetSlot,
        resolvedSlotId,
        componentSlotIds: allComponents.map(c => c.slotId),
        validSlotIds
      });
      
      // First, try to filter by exact slotId match
      let slotComponents = allComponents
        .filter(comp => {
          const compSlotId = comp.slotId || mainContentSlotId;
          // Match by exact slot ID or by common aliases
          const matches = compSlotId === resolvedSlotId || 
                         compSlotId === (targetSlotId || targetSlot) ||
                         (compSlotId === 'body' && resolvedSlotId === 'content') ||
                         (compSlotId === 'content' && resolvedSlotId === 'body') ||
                         (compSlotId === 'body' && resolvedSlotId === 'body') ||
                         (compSlotId === 'content' && resolvedSlotId === 'content');
          if (matches) {
            logger.log('✅ [AppPreview] Component matches slot', {
              componentId: comp.id,
              componentType: comp.type,
              compSlotId,
              resolvedSlotId
            });
          }
          return matches;
        });
      
      // If no components matched, include ALL components from the page
      // This handles cases where components don't have slotId set or use different slot names
      if (slotComponents.length === 0 && allComponents.length > 0) {
        logger.log('⚠️ [AppPreview] No components matched slot, including all components from page', {
          requestedSlot: targetSlotId || targetSlot,
          resolvedSlotId,
          totalComponents: allComponents.length,
          componentTypes: allComponents.map(c => c.type),
          componentSlotIds: allComponents.map(c => c.slotId)
        });
        slotComponents = allComponents;
      }
      
      // Map components and ensure slotId is set correctly
      slotComponents = slotComponents.map(comp => ({
        ...comp,
        slotId: resolvedSlotId // Ensure slotId is set correctly for target slot
      }));
      
      logger.log('✅ [AppPreview] Found components for slot', { 
        inputSlot: targetSlotId || targetSlot,
        resolvedSlotId,
        componentCount: slotComponents.length,
        componentTypes: slotComponents.map(c => c.type)
      });
      
      // Update only the target slot (using business-friendly name for state)
      setSlotContent(prev => {
        const updated = { ...prev };
        const existingSlot = prev[resolvedSlotId] || {
          components: [],
          layoutId: currentLayoutId || layoutSlots[0]?.id || 'single-column',
          themeId: currentThemeId || 'modern',
          pageName: activePageName || targetPage
        };

        const baseComponents = clearSlot !== false ? [] : (existingSlot.components || []);
        const nextComponents = [...baseComponents, ...slotComponents];
        updated[resolvedSlotId] = {
          ...existingSlot,
          components: nextComponents,
          layoutId: targetPageData.layoutId || existingSlot.layoutId || currentLayoutId,
          themeId: targetPageData.themeId || existingSlot.themeId || currentThemeId,
          pageName: targetPageData.pageName || existingSlot.pageName || targetPage
        };

        logger.log('🔄 [AppPreview] Slot updated', {
          resolvedSlotId,
          clearSlot,
          componentCount: nextComponents.length
        });

        return updated;
      });
      
      // Update layout/theme/page metadata if changed
      if (targetPageData.layoutId) setCurrentLayoutId(targetPageData.layoutId);
      if (targetPageData.themeId) setCurrentThemeId(targetPageData.themeId);
      setActivePageName(targetPageData.pageName || targetPageData.name || targetPage);
      
    };
    
    console.log('👂 [AppPreview] Setting up slotNavigation event listener', { appName });
    window.addEventListener('slotNavigation', handleSlotNavigation);
    
    return () => {
      console.log('👋 [AppPreview] Removing slotNavigation event listener');
      window.removeEventListener('slotNavigation', handleSlotNavigation);
    };
  }, [appName, currentLayoutId, currentThemeId, activePageName, pages]);

  // Listen for page updates from builder
  useEffect(() => {
    const handle = (e) => {
      if (e.origin !== window.location.origin || e.data?.type !== 'UPDATE_PREVIEW') return;
      const pageData = e.data.pageData;
      if (!pageData) return;
      setPages(prev => {
        const exists = prev.some(p => (p.id && pageData.id && p.id === pageData.id) ||
          (p.pageName && pageData.pageName && p.pageName === pageData.pageName));
        if (exists) {
          return prev.map(p => (
            (p.id && pageData.id && p.id === pageData.id) ||
            (p.pageName && pageData.pageName && p.pageName === pageData.pageName)
              ? pageData
              : p
          ));
        }
        return [...prev, pageData];
      });
      initializeFromPage(pageData);
      setLoading(false);
    };
    window.addEventListener('message', handle);
    return () => window.removeEventListener('message', handle);
  }, [initializeFromPage]);

  const allComponents = useMemo(() => {
    // Flatten components while preserving their slotId
    const components = [];
    Object.keys(slotContent).forEach(slotId => {
      const slot = slotContent[slotId];
      if (slot && slot.components) {
        slot.components.forEach(comp => {
          // Ensure each component has the correct slotId
          components.push({
            ...comp,
            slotId: comp.slotId || slotId
          });
        });
      }
    });
    return components;
  }, [slotContent]);

  // Callback to update components when PreviewRenderer handles slot navigation
  const handleUpdateComponents = useCallback((updatedComponents) => {
    // Update slotContent based on updated components
    // PreviewRenderer already handles slot clearing/merging, so we just need to organize the components
    const slots = currentLayoutId ? getLayoutSlots(currentLayoutId) : [];
    const newSlotContent = {};
    
    // Initialize all slots with empty arrays
    slots.forEach(slot => {
      newSlotContent[slot.id] = {
        components: [],
        layoutId: currentLayoutId || 'single-column',
        themeId: currentThemeId || 'modern',
        pageName: activePageName || 'Page'
      };
    });
    
    // Assign components to their slots
    updatedComponents.forEach(comp => {
      const slotId = comp.slotId || slots.find(s => s.id === 'content' || s.id === 'body')?.id || slots[0]?.id || 'body';
      if (!newSlotContent[slotId]) {
        // If slot doesn't exist in layout, create it
        newSlotContent[slotId] = {
          components: [],
          layoutId: currentLayoutId || 'single-column',
          themeId: currentThemeId || 'modern',
          pageName: activePageName || 'Page'
        };
      }
      newSlotContent[slotId].components.push(comp);
    });
    
    logger.log('🔄 [AppPreview] Components updated via PreviewRenderer callback', {
      totalComponents: updatedComponents.length,
      slotContent: Object.keys(newSlotContent).map(slotId => ({
        slotId,
        componentCount: newSlotContent[slotId].components.length
      }))
    });
    
    setSlotContent(newSlotContent);
  }, [currentLayoutId, currentThemeId, activePageName]);

  if (loading) return <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100vh' }}>Loading {appName}...</div>;
  if (!appName || !pages.length) return <div style={{ maxWidth:1200, margin:'40px auto', padding:24 }}><Tip bg="#FEF2F2" txt="#991B1B">No pages found</Tip></div>;

  // Ensure we have a valid layoutId
  const effectiveLayoutId = currentLayoutId || 'single-column';
  const effectiveThemeId = currentThemeId || 'modern';

  return (
    <div style={{ width: '100%', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <Tip bg="#EFF6FF" txt="#1E3A8A">
            Slot-based Navigation Active • Header & Footer are persistent, Body is swappable
          </Tip>
        </div>
        <PreviewRenderer
          components={allComponents}
          layoutId={effectiveLayoutId}
          themeId={effectiveThemeId}
          onComponentClick={()=>{}}
          selectedComponentId={null}
          isProductionMode={true}
          onUpdateComponents={handleUpdateComponents}
        />
      </div>
    </div>
  );
}